/**
 * The late chore rush (docs/bounties.md "Late chore rush", flag `lateRushUI`). Owner, 2026-10-11:
 * "Notifications for missed chores are spammy, rather than after x time for each task they expire,
 * batch them and run a gold rush for the late tasks", then "The late batches should be per
 * individual, so they can display during their most active times".
 *
 * With the flag on the sweep sends no "Overdue" or "Missed" push. Instead each peon, at one of their
 * usual hours (activeHours), gets one push for every chore that is late, and for an hour every late
 * chore pays them double (the overdue reminder's 2x, shared/bounty.ts reminderInForce).
 *
 * Pure and dependency-free, like shared/bounty.ts: the server, both clients and the Node tests run it.
 */
import type { LateRush } from "./types";

const HOUR_MS = 3_600_000;

/** How long one peon's window stays open. */
export const LATE_RUSH_MS = HOUR_MS;
/** A peon's next window waits at least this long after their last one started. */
export const LATE_RUSH_GAP_MS = 3 * HOUR_MS;
/** How far back a peon's completions count towards their usual hours. */
export const LATE_RUSH_HISTORY_MS = 60 * 24 * HOUR_MS;
/** Fewer completions than this in LATE_RUSH_HISTORY_MS, and the default hour stands. */
export const LATE_RUSH_MIN_HISTORY = 5;
/** The usual hour of a peon without much history: 6 pm, household time. */
export const LATE_RUSH_DEFAULT_HOUR = 18;
/** How many of a peon's busiest hours their windows may start in. */
export const LATE_RUSH_HOURS = 2;

/**
 * A peon's usual hours: given the household-local hour of day (0 to 23) of each of their completions
 * in the last LATE_RUSH_HISTORY_MS, their LATE_RUSH_HOURS busiest hours, ties to the earlier hour,
 * in that order. With fewer than LATE_RUSH_MIN_HISTORY completions, [LATE_RUSH_DEFAULT_HOUR].
 */
export function activeHours(hours: readonly number[]): number[] {
  const valid = hours.filter((h) => Number.isInteger(h) && h >= 0 && h < 24);
  if (valid.length < LATE_RUSH_MIN_HISTORY) return [LATE_RUSH_DEFAULT_HOUR];
  const counts = new Array<number>(24).fill(0);
  for (const h of valid) counts[h]++;
  return counts
    .map((n, h) => ({ h, n }))
    .filter((x) => x.n > 0)
    .sort((a, b) => b.n - a.n || a.h - b.h)
    .slice(0, LATE_RUSH_HOURS)
    .map((x) => x.h);
}

/** Whether `rush`'s window contains `at` (ms since epoch): from starts_at, until ends_at. Null means none. */
export function isLateRushActive(rush: Pick<LateRush, "starts_at" | "ends_at"> | null | undefined, at: number): boolean {
  if (!rush) return false;
  return Date.parse(rush.starts_at) <= at && at < Date.parse(rush.ends_at);
}

// Copy, shared by the server's push, both clients' banners and the workshop. Plain and short.

/** The push's title: the chore's name for one, the count for more ("Gold rush: 3 late chores"). */
export function lateRushPushTitle(count: number, firstTitle: string): string {
  return count === 1 ? `Gold rush: ${firstTitle}` : `Gold rush: ${count} late chores`;
}
/** The push's body. */
export const LATE_RUSH_PUSH_BODY = "Double gold on them for an hour.";
/** The banner's head while the viewer's window is open. */
export const LATE_RUSH_TITLE = "Gold rush: late chores";
/** The banner's line under it, counting down to the viewer's ends_at. */
export function lateRushBody(mins: number): string {
  return `Double gold on every late chore for ${mins} min.`;
}
/** The workshop's quiet line after "Start a late rush now". */
export function lateRushStartedLine(count: number): string {
  return `Gold rush on ${count} late ${count === 1 ? "chore" : "chores"}.`;
}
/** The 409 when nothing is late. */
export const NOTHING_LATE = "Nothing is late.";
